import type { InvoicingRepository, Page, WriteResult } from "@estelle/domain";

/**
 * Invoicing, for a build that has an API — which has none for it yet.
 *
 * GST is postponed (M12-6), so there is no `/api/invoices` to call. Until
 * there is, this answers honestly rather than inventing: every read is an
 * empty result and every write is refused with a reason a screen can show.
 *
 * It used to be the fixture implementation, reached through a spread of
 * `createFixtureDataSource()` in `createApiDataSource`. That put a fixture
 * adapter, its whole dataset and a fixture sign-in into every deployed
 * bundle, with fake invoices on screen under an API that had never heard of
 * them (#163). A build with `VITE_API_URL` set must contain no fixture code,
 * so this stands in until the real routes land — and the day they do, the
 * body of `createApiInvoicing` becomes calls to them and `invoicingIsLive`
 * in `adapters/api/index.ts` flips beside it.
 */

const UNAVAILABLE = "GST invoicing is not available yet.";

const EMPTY_PAGE: Page<never> = { items: [], nextCursor: null, total: 0 };

function empty<T>(): Promise<Page<T>> {
  return Promise.resolve(EMPTY_PAGE);
}

/**
 * `blocked` rather than `invalid`: nothing is wrong with what the user
 * entered, the precondition — that invoicing exists — is what fails.
 */
function unavailable<T>(): Promise<WriteResult<T>> {
  return Promise.resolve({
    ok: false,
    rejection: { kind: "blocked", message: UNAVAILABLE },
  });
}

export function createApiInvoicing(): InvoicingRepository {
  return {
    list: () => empty(),
    get: () => Promise.resolve(null),
    listUninvoicedOrders: () => empty(),
    generateFromOrder: () => unavailable(),
    issue: () => unavailable(),
    cancel: () => unavailable(),
    listCreditNotes: () => empty(),
    getCreditNote: () => Promise.resolve(null),
    createCreditNote: () => unavailable(),
    exportXlsx: () => unavailable(),
    exportHsnSummary: () => unavailable(),
  };
}
